import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Download, Loader2 } from "lucide-react";

import { PageHeader } from "../../../components/portfolio-risk-concentration/PageHeader";
import { PrcDialogContent } from "../../../components/portfolio-risk-concentration/PrcDialogContent";
import { StepShell } from "../../../components/portfolio-risk-concentration/StepShell";
import { Badge } from "../../../ui/badge";
import { Button } from "../../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Dialog, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../../ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../ui/dropdown-menu";
import { Skeleton } from "../../../ui/skeleton";
import { Switch } from "../../../ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import { Textarea } from "../../../ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../ui/tooltip";
import { apiDetail } from "../../../utils/portfolio-risk-concentration/api/scenario";
import {
  downloadAttestationFile,
  downloadAttestationFiles,
  useAttestationScope,
  useAttestationStore,
  type AttestationFile,
  type AttestationScopeRow,
  type AttestationPeriod,
  type AttestationRow,
} from "../../../utils/portfolio-risk-concentration/api/attestation";
import { cn } from "../../../utils/common/cn";
import { hasTier } from "../../../components/portfolio-risk-concentration/access-control";

const STATUS_LABEL: Record<AttestationPeriod["status"], string> = {
  PENDING_SUBMISSION: "Not submitted",
  PENDING_REVIEW: "Awaiting review",
  APPROVED: "Approved",
  RETURNED: "Returned",
  SUPERSEDED: "Superseded",
};

function PeriodBadge({ status }: { status: AttestationPeriod["status"] }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.1em]",
        status === "APPROVED" &&
          "border-[color:var(--approve)]/40 bg-[color:var(--approve)]/10 text-[color:var(--approve)]",
        status === "RETURNED" && "border-destructive/50 bg-destructive/10 text-destructive",
        (status === "PENDING_REVIEW" || status === "PENDING_SUBMISSION") &&
          "border-amber/50 bg-amber-dim/40 text-amber",
        status === "SUPERSEDED" && "text-muted-foreground",
      )}
    >
      {STATUS_LABEL[status]}
    </Badge>
  );
}

/** "12 days": whole days since the month ended. */
function pendingSince(days: number | null): string {
  if (days === null) return "—";
  if (days <= 0) return "Today";
  return `${days} day${days === 1 ? "" : "s"}`;
}

/** The row's month, investor and facility: what a dialog names so nobody signs the wrong one. */
function rowTitle(r: AttestationPeriod): string {
  return `${r.investorName} · ${r.facilityName} · ${r.periodLabel ?? "—"}`;
}

const HEAD_CLASS = "text-[11px] uppercase tracking-[0.12em]";

type DialogState = { kind: "submit" | "decide"; row: AttestationPeriod } | null;

export default function AttestationPage() {
  const canSubmit = hasTier("reviewer");
  const canDecide = hasTier("contributor");
  const store = useAttestationStore();
  const scope = useAttestationScope();
  const [switching, setSwitching] = useState<string | null>(null);

  async function toggleScope(row: AttestationScopeRow, enabled: boolean) {
    const key = `${row.investorCode}:${row.facilityCode}`;
    setSwitching(key);
    setError(null);
    try {
      await scope.setEnabled(row, enabled);
    } catch (err) {
      setError(apiDetail(err, "Could not change the attestation switch."));
    } finally {
      setSwitching(null);
    }
  }

  const [dialog, setDialog] = useState<DialogState>(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function openDialog(next: DialogState) {
    setNotes("");
    setError(null);
    setDialog(next);
  }

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      setDialog(null);
      setNotes("");
    } catch (err) {
      setError(apiDetail(err, "Could not save."));
    } finally {
      setBusy(false);
    }
  }

  /** One file of a row, or (no `file`) all three one after another. The server streams each; the first failure stops the rest. */
  async function download(row: AttestationPeriod, file?: AttestationFile) {
    setDownloading(row.periodId);
    setError(null);
    try {
      if (file) await downloadAttestationFile(row.periodId, file);
      else await downloadAttestationFiles(row);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not download the files.");
    } finally {
      setDownloading(null);
    }
  }

  /** One figure of the month-end counts: "…" while it is on its way, a dash when the row has none, with the reason as its tooltip. */
  const count = (r: AttestationRow, value: number | null, className?: string) => (
    <TableCell className={cn("tabular-nums", className)} title={value === null && r.countsError ? r.countsError : undefined}>
      {value === null ? (r.countsLoading ? "…" : "—") : value}
    </TableCell>
  );

  /** A menu of the row's three files, each downloaded on its own, and "All three files" for the lot, one after another. */
  const downloadButton = (r: AttestationPeriod) =>
    canSubmit && r.downloadable && r.files.length > 0 ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1.5"
            disabled={downloading === r.periodId}
            title="Download the tables of this month-end"
          >
            {downloading === r.periodId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            Download
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {r.files.map((file) => (
            <DropdownMenuItem key={file.kind} onSelect={() => download(r, file)}>
              {file.label}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => download(r)}>All {r.files.length} files, one by one</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ) : null;

  const submitting = dialog?.kind === "submit" ? dialog.row : null;
  const deciding = dialog?.kind === "decide" ? dialog.row : null;

  return (
    <StepShell step={4}>
      <PageHeader
        title="Attestation"
        subtitle="Confirm each month-end's numbers are final. Every investor and facility is attested separately, month by month, until it is approved."
      />

      {error && (
        <div role="alert" className="mt-4 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <Card className="mt-5">
        <CardHeader>
          <CardTitle className="text-sm font-semibold">Awaiting attestation</CardTitle>
        </CardHeader>
        <CardContent>
          {store.isError ? (
            <p role="alert" className="text-sm text-destructive">
              Could not load the attestations{store.error instanceof Error ? `: ${store.error.message}` : "."}
            </p>
          ) : store.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : store.open.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing awaiting attestation.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    {[
                      "Month-Year",
                      "Investor",
                      "Facility",
                      "Red",
                      "Amber",
                      "Green",
                      "Not measured",
                      "Needs attention",
                      "Pending changes",
                      "Pending since",
                      "Status",
                      "",
                    ].map((h, i) => (
                      <TableHead key={`${h}-${i}`} className={HEAD_CLASS}>
                        {h}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {store.open.map((r) => (
                    <TableRow key={r.periodId}>
                      <TableCell className="whitespace-nowrap font-medium tabular-nums">{r.periodLabel ?? "—"}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.investorName}</TableCell>
                      <TableCell className="whitespace-nowrap font-medium">{r.facilityName}</TableCell>
                      {count(r, r.red, "text-destructive")}
                      {count(r, r.amber, "text-amber")}
                      {count(r, r.green, "text-[color:var(--approve)]")}
                      {/* Muted, not amber: a gap in our measurement is not a portfolio warning. Zero reads as an em dash so a facility
                          with full coverage does not display a column of noughts. */}
                      <TableCell
                        className="tabular-nums text-muted-foreground"
                        title={r.notMeasured === null && r.countsError ? r.countsError : undefined}
                      >
                        {r.notMeasured === null ? (r.countsLoading ? "…" : "—") : r.notMeasured === 0 ? "—" : r.notMeasured}
                      </TableCell>
                      <TableCell className="max-w-[300px]">
                        {!r.attention || r.attention.length === 0 ? (
                          <span className="text-muted-foreground">{r.attention === null && r.countsLoading ? "…" : "—"}</span>
                        ) : (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="block truncate text-xs text-muted-foreground">{r.attention.join(", ")}</span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-sm">{r.attention.join(", ")}</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </TableCell>
                      <TableCell>
                        {r.pendingChanges > 0 && r.manageHref ? (
                          <Link to={r.manageHref} className="whitespace-nowrap text-xs text-amber hover:underline">
                            {r.pendingChanges} pending →
                          </Link>
                        ) : (
                          <span className="text-xs text-muted-foreground">0</span>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap tabular-nums">{pendingSince(r.pendingDays)}</TableCell>
                      <TableCell>
                        <PeriodBadge status={r.status} />
                        {r.status === "RETURNED" && r.reviewerNotes && (
                          <div className="mt-1 max-w-[200px] text-[11px] text-muted-foreground">{r.reviewerNotes}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          {downloadButton(r)}
                          {(r.status === "PENDING_SUBMISSION" || r.status === "RETURNED") && canSubmit && (
                            <Button size="sm" className="h-8" onClick={() => openDialog({ kind: "submit", row: r })}>
                              Submit
                            </Button>
                          )}
                          {r.status === "PENDING_REVIEW" && canDecide && (
                            <Button size="sm" className="h-8" onClick={() => openDialog({ kind: "decide", row: r })}>
                              Approve
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-sm font-semibold">Signed-off log</CardTitle>
        </CardHeader>
        <CardContent>
          {store.signedOff.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing has been signed off yet.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    {["Month-Year", "Investor", "Facility", "Submitted by", "Approved by", "Approved on", ""].map((h, i) => (
                      <TableHead key={`${h}-${i}`} className={HEAD_CLASS}>
                        {h}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {store.signedOff.map((r) => (
                    <TableRow key={r.periodId}>
                      <TableCell className="whitespace-nowrap align-top font-medium tabular-nums">{r.periodLabel ?? "—"}</TableCell>
                      <TableCell className="whitespace-nowrap align-top">{r.investorName}</TableCell>
                      <TableCell className="whitespace-nowrap align-top font-medium">{r.facilityName}</TableCell>
                      <TableCell className="align-top">
                        {r.submittedByRole ? (
                          <div className="max-w-[260px]">
                            <div className="text-xs">{r.submittedByRole}</div>
                            <div className="text-[11px] text-muted-foreground">{r.submitterNotes}</div>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="align-top">
                        {r.reviewedByRole ? (
                          <div className="max-w-[260px]">
                            <div className="text-xs">{r.reviewedByRole}</div>
                            <div className="text-[11px] text-muted-foreground">{r.reviewerNotes}</div>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap align-top tabular-nums">
                        {(r.reviewedAt ?? r.effectiveFrom ?? "").slice(0, 10) || "—"}
                      </TableCell>
                      <TableCell className="align-top">
                        <div className="flex justify-end">{downloadButton(r)}</div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-sm font-semibold">Manage attestation</CardTitle>
          <p className="text-xs text-muted-foreground">
            Turn an investor and facility off and it is no longer listed under Awaiting attestation; turn it on and it is. Anything already
            signed off stays in the log.
          </p>
        </CardHeader>
        <CardContent>
          {scope.isError ? (
            <p role="alert" className="text-sm text-destructive">
              Could not load the investors and facilities{scope.error instanceof Error ? `: ${scope.error.message}` : "."}
            </p>
          ) : scope.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : scope.rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No investors or facilities to attest.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    {["Investor", "Facility", "Attestation", "Last updated"].map((h) => (
                      <TableHead key={h} className={HEAD_CLASS}>
                        {h}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {scope.rows.map((row) => {
                    const key = `${row.investorCode}:${row.facilityCode}`;
                    return (
                      <TableRow key={key}>
                        <TableCell className="whitespace-nowrap">{row.investorName}</TableCell>
                        <TableCell className="whitespace-nowrap font-medium">{row.facilityName}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={row.enabled}
                              disabled={!canDecide || switching === key}
                              onCheckedChange={(on) => toggleScope(row, on)}
                              aria-label={`Attestation for ${row.investorName} ${row.facilityName}`}
                              title={canDecide ? undefined : "Only an approver can change this"}
                            />
                            <span className="w-8 text-xs text-muted-foreground">{row.enabled ? "On" : "Off"}</span>
                            {switching === key && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap tabular-nums">
                          {row.updatedAt ? (
                            <>
                              {row.updatedAt.replace("T", " ").slice(0, 16)}
                              {row.updatedBy && <span className="ml-2 text-[11px] text-muted-foreground">{row.updatedBy}</span>}
                            </>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialog !== null} onOpenChange={(open) => !open && !busy && setDialog(null)}>
        <PrcDialogContent className="sm:max-w-lg">
          {submitting && (
            <>
              <DialogHeader>
                <DialogTitle>Submit for review</DialogTitle>
                <DialogDescription>{rowTitle(submitting)}</DialogDescription>
              </DialogHeader>
              {submitting.status === "RETURNED" && submitting.reviewerNotes && (
                <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm">
                  <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Returned with</div>
                  <p className="mt-1">{submitting.reviewerNotes}</p>
                </div>
              )}
              <Textarea
                rows={4}
                placeholder="Submitter notes — what changed this period?"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
              {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialog(null)} disabled={busy}>
                  Cancel
                </Button>
                <Button
                  disabled={!notes.trim() || busy}
                  onClick={() => run(() => store.submit(submitting.periodId, notes.trim()))}
                >
                  {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                  Submit for review
                </Button>
              </DialogFooter>
            </>
          )}
          {deciding && (
            <>
              <DialogHeader>
                <DialogTitle>Review attestation</DialogTitle>
                <DialogDescription>{rowTitle(deciding)}</DialogDescription>
              </DialogHeader>
              <div className="rounded-lg border border-border p-3">
                <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                  Submitted by {deciding.submittedByRole} ·{" "}
                  <span className="tabular-nums">{deciding.submittedAt?.slice(0, 10)}</span>
                </div>
                <p className="mt-2 text-sm">{deciding.submitterNotes}</p>
              </div>
              <Textarea
                rows={4}
                placeholder="Notes (required to approve or to return)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
              {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialog(null)} disabled={busy}>
                  Cancel
                </Button>
                <Button
                  variant="outline"
                  disabled={!notes.trim() || busy}
                  onClick={() => run(() => store.decide(deciding.periodId, "RETURNED", notes.trim()))}
                >
                  Return for rework
                </Button>
                <Button
                  disabled={!notes.trim() || busy}
                  onClick={() => run(() => store.decide(deciding.periodId, "APPROVED", notes.trim()))}
                >
                  {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                  Approve
                </Button>
              </DialogFooter>
            </>
          )}
        </PrcDialogContent>
      </Dialog>
    </StepShell>
  );
}
